import { Component, Directive, HostListener, OnInit, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Store } from '@ngrx/store';
import { BreakpointObserverService } from '../../../../services/breakpoint-observer/breakpoint-observer.service';
import { AccessTypeSelection } from '../../../../../common/enums/access-type-selection.enum';
import { ContentUtil } from '../../../../utils/content/content.util';
import { environment } from '../../../../../environments/environment';
import {
    selectGranteeAccessType,
    selectGranteeFullRegError,
} from '../../../../store/selectors/grantee.selector';
import { getAdobeConfigData } from './grantee-success-page-adobe';
import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { VgnService } from '../../../../services/vgn/vgn.service';
import { LoggerService } from '../../../../services/logger/logger.service';
import { AdobeGranteeFlowType } from '../grantee-flows.interface';
import { ClientSegmentFacadeService } from 'src/app/services/client-segment-observer/client-segment.service';
import { ClientSegmentContentDTO } from 'src/common/dtos/client-segment.dto';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { ButtonModule } from '@vg-constellation/angular-20/button';
import { BannerModule } from '@vg-constellation/angular-20/banner';
import { IconModule } from '@vg-constellation/angular-20/icon';

const SHARED_IMPORTS = [
    AsyncPipe,
    NgClass,
    NgIf,
    ButtonModule,
    BannerModule,
    IconModule,
];
@Directive()
class GranteeSuccessPageComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly breakpointObserverService = inject(BreakpointObserverService);
    private readonly vgnService = inject(VgnService);
    private readonly clientSegmentFacadeService = inject(ClientSegmentFacadeService);
    private window = inject(Window);
    private logger = inject(LoggerService);

    readonly accessTypeSelection = AccessTypeSelection;

    accessLevel$: Observable<AccessTypeSelection> = this.store.select(selectGranteeAccessType);

    forceSurveyZeroHeight: boolean = true;

    @HostListener('window:MDigital_Form_Displayed')
    removeForcedHeightZero() {
        this.forceSurveyZeroHeight = false;
    }

    isMobileView$: Observable<boolean> = this.breakpointObserverService.isMobileView$;

    dashboardUrl: string = environment.DASHBOARD_URL;

    granteeSuccessPageContentInfoOnly =
        ContentUtil.granteeFragmentsContent.frmcGranteeFlow.scssInformationOnlyAccess;
    granteeSuccessPageContentLimitedOnly =
        ContentUtil.granteeFragmentsContent.frmcGranteeFlow.scssLimitedAuthority;
    fullRegEndpointFailureMessageContent =
        ContentUtil.granteeFragmentsContent.abilityToTransactErrorMessage;

    fullRegEndpointFailureMessage: Observable<string>;

    accessLevelContent$ = this.accessLevel$.pipe(
        map((accessLevel) => {
            if (accessLevel === AccessTypeSelection.INFORMATION_ONLY) {
                return this.granteeSuccessPageContentInfoOnly;
            } else {
                return this.granteeSuccessPageContentLimitedOnly;
            }
        }),
    );

    hasFullRegError$ = this.store.select(selectGranteeFullRegError);

    ngOnInit(): void {
        this.scrollTop();
        setTimeout(() => {
            this.loadSurvey();
        }, 1000);

        this.buildFailureMessage();
    }

    goToDashboard(): void { }

    private loadSurvey(): void {
        const nativeWindow: Window | any = this.window;
        if (
            nativeWindow?.KAMPYLE_ONSITE_SDK &&
            typeof nativeWindow.KAMPYLE_ONSITE_SDK?.updatePageView === 'function'
        ) {
            nativeWindow.KAMPYLE_ONSITE_SDK.updatePageView();
        } else {
            this.logger.warn('Failed to load Medallia SDK');
        }
    }

    private scrollTop(): void {
        window.scrollTo(0, 0);
    }

    private buildFailureMessage(): void {
        this.fullRegEndpointFailureMessage =
            this.clientSegmentFacadeService.clientSegmentContent$.pipe(
                map((content: ClientSegmentContentDTO) => {
                    return this.fullRegEndpointFailureMessageContent.text1.replace(
                        '{phoneNumber}',
                        content?.phoneNumber,
                    );
                }),
            );
    }
}

@AdobeTagging(getAdobeConfigData(AdobeGranteeFlowType.EXISTING_LIMITED))
@Component({
    selector: 'zci-existing-grantee-success-page',
    templateUrl: './grantee-success-page.component.html',
    styleUrls: ['grantee-success-page.component.scss'],
    imports: SHARED_IMPORTS,
})
export class ExistingGranteeSuccessPageComponent extends GranteeSuccessPageComponent { }

@AdobeTagging(getAdobeConfigData(AdobeGranteeFlowType.NEW_INFO_ONLY))
@Component({
    selector: 'zci-new-grantee-success-page',
    templateUrl: './grantee-success-page.component.html',
    styleUrls: ['grantee-success-page.component.scss'],
    imports: SHARED_IMPORTS,
})
export class NewGranteeInfoOnlySuccessPageComponent extends GranteeSuccessPageComponent { }

@AdobeTagging(getAdobeConfigData(AdobeGranteeFlowType.NEW_LIMITED))
@Component({
    selector: 'zci-new-grantee-success-page',
    templateUrl: './grantee-success-page.component.html',
    styleUrls: ['grantee-success-page.component.scss'],
    imports: SHARED_IMPORTS,
})
export class NewGranteeLimitedSuccessPageComponent extends GranteeSuccessPageComponent { }

@AdobeTagging(getAdobeConfigData(AdobeGranteeFlowType.NON_VG_CLIENT_INFO_ONLY))
@Component({
    selector: 'zci-new-grantee-success-page',
    templateUrl: './grantee-success-page.component.html',
    styleUrls: ['grantee-success-page.component.scss'],
    imports: SHARED_IMPORTS,

})
export class NvcInfoOnlySuccessPageComponent extends GranteeSuccessPageComponent { }

@AdobeTagging(getAdobeConfigData(AdobeGranteeFlowType.NON_VG_CLIENT_LIMITED))
@Component({
    selector: 'zci-new-grantee-success-page',
    templateUrl: './grantee-success-page.component.html',
    styleUrls: ['grantee-success-page.component.scss'],
    imports: SHARED_IMPORTS,
})
export class NvcLimitedSuccessPageComponent extends GranteeSuccessPageComponent { }
